import { detectOS, detectArch } from "../funnel/detectOS";
import { INSTALLERS, PLATFORM_KEYS, pickInstaller } from "../funnel/installers";

/**
 * Public download page. Reachable WITHOUT a login or an active subscription
 * (rendered outside SignInGate/PlanGate in main.tsx) — Steerly is free to
 * download; the subscription gate is enforced when you OPEN the app (the app
 * itself runs SignInGate → PlanGate). So this view is intentionally
 * entitlement-agnostic: it serves the installer + the "sign in and subscribe
 * in the app" steps.
 *
 * Mac, Windows, and Linux (AppImage, x86_64 + aarch64) all ship today. The
 * detected platform/arch is featured; the others are one click away.
 */
export function DownloadView({
  userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "",
}: {
  userAgent?: string;
}) {
  const os = detectOS(userAgent);
  const arch = detectArch(userAgent);
  const primaryKey = pickInstaller(os, arch);
  const primary = INSTALLERS[primaryKey];
  const others = PLATFORM_KEYS.filter((key) => key !== primaryKey);

  let firstLaunch: string;
  if (primary.platform === "Mac") {
    firstLaunch = "On Mac, right-click the app → Open → Open.";
  } else if (primary.platform === "Windows") {
    firstLaunch = "On Windows, click More info → Run anyway.";
  } else {
    firstLaunch =
      "On Linux, run chmod +x Steerly-*.AppImage then launch it (WebKitGTK required — install libwebkit2gtk-4.1 on minimal distros).";
  }

  return (
    <div className="download-view">
      <h1>Download Steerly.</h1>
      <p className="download-view__lede">
        Free to download. Open the app, sign in, and choose a plan — Base, Pro, or Ultra Security —
        to start. Every feature is gated to an active subscription.
      </p>

      <div className="download-view__card">
        <div className="download-view__os">{primary.meta}</div>
        <a className="download-view__btn" href={primary.url}>
          ↓ {primary.label}
        </a>
        <p className="download-view__alt">
          <span>On a different OS?</span>{" "}
          {others.map((key, i) => (
            <span key={key}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              <a href={INSTALLERS[key].url}>{INSTALLERS[key].label} →</a>
            </span>
          ))}
        </p>
      </div>

      <div className="download-view__note">
        <b>First launch:</b> we're finalizing code-signing. {firstLaunch} After that it opens
        normally.
      </div>

      <ol className="download-view__steps">
        <li>Install &amp; open Steerly.</li>
        <li>Sign in and pick a plan — your subscription unlocks every feature.</li>
        <li>
          Connect your agents: run <code>agentguard install-shims</code> or use the in-app
          installer.
        </li>
        <li>Start a session — every command is now classified, gated, and logged.</li>
      </ol>
    </div>
  );
}
